import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const Chip = ({ children }) => (
  <span className="rounded-full bg-cream border border-ochre/40 px-4 py-2 text-[13px] font-semibold text-earth">{children}</span>
);

const ProgramSection = ({ num, name, sub, desc, children, flip, dark, tag = "In Development" }) => (
  <section className={`px-6 py-16 sm:py-24 ${dark ? "bg-charcoal text-cream grain relative overflow-hidden" : "bg-cream"}`}>
    <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
      <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <span className={`font-display italic text-6xl sm:text-7xl font-light ${dark ? "text-ochre/50" : "text-terracotta/40"}`}>{num}</span>
        <h2 className={`mt-4 font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] ${dark ? "text-cream" : "text-charcoal"}`}>{name}</h2>
        {sub && <p className={`mt-4 text-xs uppercase tracking-[0.25em] font-bold ${dark ? "text-ochre" : "text-terracotta"}`}>{sub}</p>}
        <div className="mt-6 flex flex-wrap gap-3">
          <Tag tone={dark ? "cream" : "terracotta"}>{tag}</Tag>
        </div>
        <p className={`mt-7 text-base lg:text-lg leading-relaxed ${dark ? "text-cream/80" : "text-earth"}`}>{desc}</p>
        <p className={`mt-6 text-sm leading-relaxed ${dark ? "text-cream/60" : "text-earth/70"}`}>
          Access: privately, through NDIS funding where eligible, or service-funded referrals.{" "}
          <Link to="/approach" className={`font-semibold underline underline-offset-4 ${dark ? "text-ochre" : "text-terracotta"}`}>How our approach works</Link>
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link data-testid={`program-${num}-request-btn`} to="/request-support" className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-105 ${dark ? "bg-ochre text-charcoal" : "bg-terracotta text-cream"}`}>
            Request Support <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link data-testid={`program-${num}-refer-btn`} to="/referrals" className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-widest border-2 transition-colors duration-300 ${dark ? "border-cream/40 text-cream hover:bg-cream/10" : "border-charcoal text-charcoal hover:bg-charcoal hover:text-cream"}`}>
            Make a Referral
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.15} className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <div className={`rounded-[40px] p-8 sm:p-12 ${dark ? "bg-cream/[0.06] border border-cream/10" : "bg-creamdark border border-ochre/25"}`}>
          {children}
        </div>
      </Reveal>
    </div>
  </section>
);

const List = ({ title, items, dark }) => (
  <div>
    {title && <p className={`text-xs uppercase tracking-[0.25em] font-bold mb-5 ${dark ? "text-ochre" : "text-terracotta"}`}>{title}</p>}
    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((it) => (
        <li key={it} className={`flex gap-3 items-start text-[15px] leading-relaxed ${dark ? "text-cream/85" : "text-earth"}`}>
          <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${dark ? "bg-ochre" : "bg-terracotta"}`} />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

const Programs = () => (
  <div data-testid="programs-page">
    <PageHero
      kicker="Programs"
      title={"Support that meets life\nwhere it's happening."}
      lede="Six proposed programs, one relational model. Each is being shaped through community consultation — referrals are welcomed based on what is happening in a person's life, not on picking the perfect program."
    />

    <section className="px-6 pt-14 sm:pt-20 bg-cream">
      <Reveal>
        <div className="max-w-7xl mx-auto rounded-[40px] bg-blush/40 border border-blush p-9 sm:p-12">
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <Tag tone="terracotta">In development — 10–12 week structured programs</Tag>
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl leading-snug text-charcoal max-w-3xl">
            Structured programs <span className="italic text-terracotta">and</span> individualised support.
          </h2>
          <p className="mt-5 text-earth/85 leading-relaxed max-w-3xl">
            Turning Story, Education Outside the Box and Those Who Know, Not the Adults That Don't are being
            developed as structured 10–12 week programs — with an intended duration, key themes, group yarning
            opportunities and guided activities. The group components are structured, while individual work adapts
            to each person's needs, strengths, interests and circumstances. Joining a program never locks you in —
            other areas of support can be explored, subject to appropriate agreements and consent.
          </p>
          <p className="mt-4 text-earth/85 leading-relaxed max-w-3xl">
            And you never need a program to work with us — individual support is available through{" "}
            <Link to="/private-supports" className="font-bold text-terracotta underline decoration-ochre decoration-2 underline-offset-4 hover:text-earth transition-colors">private supports</Link>,{" "}
            <Link to="/ndis-supports" className="font-bold text-terracotta underline decoration-ochre decoration-2 underline-offset-4 hover:text-earth transition-colors">NDIS supports</Link>{" "}
            and <Link to="/referrals" className="font-bold text-terracotta underline decoration-ochre decoration-2 underline-offset-4 hover:text-earth transition-colors">service-funded referrals</Link>.
          </p>
        </div>
      </Reveal>
    </section>

    <ProgramSection
      num="01"
      name="Those Who Know, Not the Adults That Don't"
      sub="Placement Stability Focus"
      desc="This program challenges the idea that adults and professionals automatically hold the most important knowledge about children's experiences. Children and young people who have experienced care, placement changes, family separation and complex systems hold knowledge that adults often do not. Those Who Know creates opportunities for that knowledge to be recognised, documented and shared safely."
    >
      <List title="Focus areas" items={[
        "Placement stability", "Relationships between children and carers", "Supporting kinship carers",
        "Supporting foster carers", "Understanding behaviour through story and context", "Identity & belonging",
        "Family relationships", "Placement transitions", "Emotional regulation", "Strengthening children's voices",
        "Reducing pressure on carers", "Practical support during difficult periods",
      ]} />
      <div className="mt-8">
        <List title="How we work" items={[
          "Narrative conversations", "Letter writing", "Outsider witnessing", "shared wisdom", "Peer knowledge",
          "Creative storytelling", "Mentoring & movement", "Yarning circles", "Carer conversations", "Practical advocacy",
        ]} />
      </div>
      <p className="mt-8 rounded-3xl bg-terracotta/10 border border-terracotta/25 px-6 py-5 font-display italic text-xl text-charcoal">
        The people who have lived it hold knowledge that the adults designing responses may not.
      </p>
    </ProgramSection>

    <ProgramSection
      num="02"
      name="Turning Story"
      sub="Substance Use, Identity & Narrative Mentoring"
      desc="Turning Story supports young people experiencing concerns around alcohol and other drug use — without ever defining a young person by substance use. We explore the relationship between the young person and the problem, and what someone who has walked this road knows that could help the next young person."
      flip dark
    >
      <List dark title="What we explore" items={[
        "What substance use is doing for the young person", "What circumstances strengthen its influence",
        "Identity outside substance use", "Harm reduction", "Relationships, culture & belonging",
        "Strengths and survival knowledge", "Hopes for the future", "Community connection",
        "Appropriate specialist referrals",
      ]} />
      <p className="mt-8 text-cream/75 text-[15px] leading-relaxed">
        Using narrative practice, mentoring, movement, letter writing and shared wisdom — walking beside young
        people, alongside (never instead of) specialist AOD services.
      </p>
    </ProgramSection>

    <ProgramSection
      num="03"
      name="Education Outside the Box"
      sub="Education Engagement Through Relationship"
      desc="A flexible alternative engagement model for children and young people struggling within traditional education environments. Disengagement from education does not automatically mean disengagement from learning — engagement can begin with relationship, identity, curiosity and belonging."
    >
      <List title="Potential program structure" items={[
        "Weekly 1:1 mentoring", "Fortnightly group engagement", "Family involvement during identified program weeks",
        "Outdoor learning", "Movement & creative activities", "Narrative conversations", "Education advocacy",
        "Practical engagement support", "shared wisdom", "Peer knowledge sharing",
      ]} />
    </ProgramSection>

    <ProgramSection
      num="04"
      name="Family Connection & Reunification"
      sub="Contact, Reconnection & Family Relationships"
      tag="Available for Support"
      desc="Supporting children, parents, carers and families around family contact, reconnection and reunification — creating space for children's experiences and hopes to be heard safely, without positioning children between competing adult narratives."
      flip dark
    >
      <List dark title="Potential supports" items={[
        "Preparing children before contact", "Post-contact support", "Narrative conversations",
        "Family storytelling", "Documenting children's hopes and concerns", "Letters & outsider witnessing",
        "Parent support", "Carer support", "Transition planning", "Relationship strengthening",
        "Family reunification work", "Practical advocacy",
      ]} />
    </ProgramSection>

    <ProgramSection
      num="05"
      name="Therapy, Mentoring & Movement"
      sub="One Hybrid Response"
      tag="Taking EOI"
      desc="Not everyone wants to sit opposite a professional in an office and talk — sometimes important conversations happen side-by-side. This hybrid response weaves narrative therapy, mentoring and movement into one flexible engagement: the relationship stays consistent while the way we work shifts around what the person needs. Movement is never just exercise — it's another environment for relationship, regulation, confidence, conversation, identity, achievement and community connection."
      flip dark
    >
      <div className="flex flex-wrap gap-3">
        {["1:1 narrative therapy", "Mentoring", "Family sessions", "Group work", "Outreach", "Walking sessions", "Movement-based sessions", "Community sessions", "Creative narrative work", "Hiking", "Gym", "Fitness", "Outdoor activities", "Group activities"].map((t) => (
          <span key={t} className="rounded-full bg-cream/10 border border-cream/20 px-4 py-2 text-[13px] font-semibold text-cream/85">{t}</span>
        ))}
      </div>
      <p className="mt-8 font-display italic text-2xl sm:text-3xl text-ochre leading-snug">
        "Sometimes it's easier to yarn when you're walking side-by-side."
      </p>
      <div data-testid="program-05-eoi-note" className="mt-8 rounded-3xl border border-ochre/30 bg-ochre/10 px-6 py-5">
        <p className="text-cream/85 text-sm leading-relaxed">
          This hybrid response is currently <strong className="text-ochre">taking expressions of interest</strong>.{" "}
          <Link data-testid="program-05-eoi-link" to="/consultation" className="font-semibold text-ochre underline underline-offset-4 hover:text-cream transition-colors">Express your interest</Link>{" "}
          — an expression of interest doesn't guarantee a place, funding, or a start date.
        </p>
      </div>
    </ProgramSection>

    <ProgramSection
      num="06"
      name="Community Programs"
      sub="Community Building Is Part of the Intervention"
      tag="Available for Support"
      desc="Community programs are not optional extras. The long-term goal is not for a child to become dependent upon a service — it is to strengthen community, relationships, culture, identity, knowledge and belonging around them."
    >
      <div className="grid sm:grid-cols-2 gap-6">
        {[
          ["Art & Cuppa with Aunties", "Relaxed creative community spaces for connection, storytelling and yarning."],
          ["Community Cook-Up", "Food, conversation, practical skills and intergenerational connection."],
          ["Yarning Circles", "Facilitated spaces to share experiences, witness each other's stories and build collective knowledge."],
          ["Community Movement", "Walking, outdoor activities and group fitness as opportunities for connection."],
        ].map(([t, d]) => (
          <div key={t} className="rounded-3xl bg-cream border border-ochre/30 p-7">
            <h3 className="font-display text-2xl text-charcoal">{t}</h3>
            <p className="mt-3 text-sm leading-relaxed text-earth/85">{d}</p>
          </div>
        ))}
      </div>
    </ProgramSection>

    <CTABand
      title="Not sure which program fits? That's okay — referrals are based on what's happening in life, not a perfect program pick."
      primary={{ to: "/referrals", label: "Make a Referral" }}
      secondary={{ to: "/contact", label: "Yarn With Us" }}
    />
  </div>
);

export default Programs;
